"use client"

import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronRight, ChevronLeft, Check, MapPin, Home, DollarSign, User, Mail, Phone, ArrowLeft } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useSearchParams } from 'next/navigation'
import Link from 'next/link'
import { trackConversion, getJourneyReport, getTrackingData } from '@/lib/user-tracking'
import { trackAdvancedConversion, getAdvancedTrackingData } from '@/lib/advanced-tracking'

interface FormData {
  email: string
  name: string
  phone: string
  hasLand: string
  landAddress?: string
  interestedArea: string
  floorplan?: string
  budget?: string
  message?: string
}

export function TypeformLeadCapture() {
  const searchParams = useSearchParams()
  const preselectedModel = searchParams.get('model')

  const [currentStep, setCurrentStep] = useState(0)
  const [formData, setFormData] = useState<FormData>({
    email: '',
    name: '',
    phone: '',
    hasLand: '',
    landAddress: '',
    interestedArea: '',
    floorplan: '',
    budget: '',
    message: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isComplete, setIsComplete] = useState(false)
  const [hasInteracted, setHasInteracted] = useState(false)
  const [hasEngaged, setHasEngaged] = useState(false)
  const [startTime] = useState(Date.now())
  const [engagementData, setEngagementData] = useState({
    scrolled: false,
    clicked: false,
    timeSpent: 0,
    maxScroll: 0,
    clickCount: 0
  })

  // Pre-fill model if provided in URL
  useEffect(() => {
    if (preselectedModel) {
      setFormData(prev => ({
        ...prev,
        floorplan: preselectedModel
      }))
    }
  }, [preselectedModel])

  // Track any user engagement
  useEffect(() => {
    const handleScroll = () => {
      setHasEngaged(true)
      const scrollPercentage = Math.round((window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100)
      setEngagementData(prev => ({
        ...prev,
        scrolled: true,
        maxScroll: Math.max(prev.maxScroll, scrollPercentage)
      }))
    }

    const handleClick = () => {
      setHasEngaged(true)
      setEngagementData(prev => ({
        ...prev,
        clicked: true,
        clickCount: prev.clickCount + 1
      }))
    }

    // Mark as engaged after 3 seconds on page
    const engagementTimer = setTimeout(() => {
      setHasEngaged(true)
    }, 3000)

    window.addEventListener('scroll', handleScroll)
    window.addEventListener('click', handleClick)

    return () => {
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('click', handleClick)
      clearTimeout(engagementTimer)
    }
  }, [])

  // Track bounce when user leaves
  useEffect(() => {
    const handleBeforeUnload = async (e: BeforeUnloadEvent) => {
      // Track if user engaged with page at all
      if (hasEngaged && !isComplete) {
        const completedFields = Object.keys(formData).filter(
          key => formData[key as keyof FormData] && formData[key as keyof FormData] !== ''
        )

        const timeOnSite = Math.floor((Date.now() - startTime) / 1000)
        const minutes = Math.floor(timeOnSite / 60)
        const seconds = timeOnSite % 60

        // Send bounce tracking data
        try {
          const trackingData = getTrackingData()
          const advancedTracking = await getAdvancedTrackingData()

          navigator.sendBeacon('/api/track-bounce', JSON.stringify({
            partialFormData: formData,
            formType: 'TypeForm - Long Form',
            currentStep,
            completedFields,
            trackingData,
            advancedTracking,
            journeyReport: getJourneyReport(),
            bounceReason: hasInteracted ? 'Started form but abandoned' : 'Viewed form but never started',
            timeOnSite: `${minutes}m ${seconds}s`,
            engagement: {
              formInteracted: hasInteracted,
              scrolled: engagementData.scrolled,
              clicked: engagementData.clicked,
              maxScroll: engagementData.maxScroll,
              clickCount: engagementData.clickCount
            }
          }))
        } catch (error) {
          console.error('Error tracking bounce:', error)
        }
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [hasEngaged, hasInteracted, isComplete, formData, currentStep, startTime, engagementData])

  // Get first name for personalization
  const firstName = formData.name ? formData.name.split(' ')[0] : ''

  const steps = [
    {
      id: 'name',
      title: "What's your name?",
      subtitle: "This helps me personalize your home search",
      icon: User,
      field: 'name',
      type: 'text',
      placeholder: 'First Last',
      required: true
    },
    {
      id: 'email',
      title: firstName ? `Perfect, ${firstName}!` : "Perfect!",
      subtitle: "Where can I send you some info?",
      icon: Mail,
      field: 'email',
      type: 'email',
      placeholder: 'email@example.com',
      required: true,
      privacyMessage: '🔒 Zero spam'
    },
    {
      id: 'phone',
      title: firstName ? `What's your phone number, ${firstName}?` : "What's your phone number?",
      subtitle: "I only reach out when it's worth your time",
      icon: Phone,
      field: 'phone',
      type: 'tel',
      placeholder: '(928) 123-4567',
      required: true,
      privacyMessage: 'We do not sell your info, ever!'
    },
    {
      id: 'hasLand',
      title: "Do you have land?",
      subtitle: "I'll show you the right options either way",
      icon: MapPin,
      field: 'hasLand',
      type: 'choice',
      choices: [
        { value: 'yes', label: 'Yes, I have a spot', icon: Check },
        { value: 'no', label: 'No, help me find one', icon: Home }
      ],
      required: false
    },
    {
      id: 'landAddress',
      title: "Where's your land?",
      subtitle: "I'll check local requirements",
      icon: MapPin,
      field: 'landAddress',
      type: 'text',
      placeholder: 'City or zip',
      condition: (data: FormData) => data.hasLand === 'yes',
      required: false,
      helperText: 'Or skip this'
    },
    {
      id: 'interestedArea',
      title: "What area are you interested in?",
      subtitle: "I can help you find a spot",
      icon: MapPin,
      field: 'interestedArea',
      type: 'text',
      placeholder: 'Any area',
      condition: (data: FormData) => data.hasLand === 'no',
      required: false,
      helperText: 'Or skip'
    },
    {
      id: 'floorplan',
      title: preselectedModel
        ? `Still like the ${preselectedModel}?`
        : "Have a floor plan in mind?",
      subtitle: preselectedModel
        ? "Or want to see others?"
        : "Totally fine if not",
      icon: Home,
      field: 'floorplan',
      type: 'text',
      placeholder: preselectedModel ? preselectedModel : 'Model name or skip',
      required: false,
      helperText: 'Skip if browsing'
    },
    {
      id: 'budget',
      title: "What's your buget?",
      subtitle: "I can help you find options that work for you",
      icon: DollarSign,
      field: 'budget',
      type: 'text',
      placeholder: '150,000',
      required: false,
      helperText: 'Skip if private'
    },
    {
      id: 'message',
      title: firstName ? `Anything else, ${firstName}?` : "Anything else?",
      subtitle: "Questions, timeline, must-haves?",
      icon: Mail,
      field: 'message',
      type: 'textarea',
      placeholder: 'Optional message to RJ',
      required: false,
      helperText: 'Or just click below'
    }
  ]

  const activeSteps = steps.filter(step => {
    if (step.condition) {
      return step.condition(formData)
    }
    return true
  })

  const currentStepData = activeSteps[currentStep]

  const handleNext = async () => {
    if (currentStep === activeSteps.length - 1) {
      await handleSubmit()
    } else {
      setCurrentStep(prev => prev + 1)
    }
  }

  const handleBack = () => {
    setCurrentStep(prev => Math.max(0, prev - 1))
  }

  // Helper to capitalize names properly
  const toTitleCase = (str: string) => {
    return str.replace(/\w\S*/g, (txt) => {
      return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });
  }

  const handleSubmit = async () => {
    setIsSubmitting(true)
    
    // Track conversion
    trackConversion('Get Started Form - Long')
    trackAdvancedConversion('Get Started Form - Long', Object.keys(formData).filter(k => formData[k as keyof FormData]))
    
    // Get tracking data
    const trackingData = getTrackingData()
    const advancedTracking = await getAdvancedTrackingData()
    const journeyReport = getJourneyReport()
    
    // Capitalize the name before sending
    const formattedData = {
      ...formData,
      name: toTitleCase(formData.name)
    }
    
    try {
      const response = await fetch('/api/lead-capture', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formattedData,
          tracking: trackingData,
          advancedTracking: advancedTracking,
          journeyReport: journeyReport
        }),
      })

      if (response.ok) {
        setIsComplete(true)

        // Fire conversion tracking for the Get Started long form with Enhanced Conversions
        if (typeof window !== 'undefined' && (window as any).gtag) {
          // Send user data for Enhanced Conversions (hashed automatically by gtag)
          (window as any).gtag('set', 'user_data', {
            'email': formData.email,
            'phone_number': formData.phone,
            'address': {
              'first_name': formData.name.split(' ')[0] || '',
              'last_name': formData.name.split(' ').slice(1).join(' ') || ''
            }
          });

          // Fire Google Ads conversion for Get Started form
          (window as any).gtag('event', 'conversion', {
            'send_to': 'AW-16837922212/LoMhCMmM_ZUbEKSb-dw-',
            'value': 5.0, // Higher value since it's a more detailed form
            'currency': 'USD'
          });

          // Also send to GA4 for tracking
          (window as any).gtag('event', 'generate_lead', {
            'currency': 'USD',
            'value': 5.0,
            'form_name': 'get_started_long_form'
          });
        }
      } else {
        throw new Error('Failed to submit form')
      }
    } catch (error) {
      console.error('Error submitting form:', error)
      alert('There was an error submitting your information. Please try again or call us directly at (928) 361-0298')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleInputChange = (value: string) => {
    setHasInteracted(true)
    setFormData(prev => ({
      ...prev,
      [currentStepData.field]: value
    }))
  }

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const validatePhone = (phone: string) => {
    // Accepts formats: (123) 456-7890, 123-456-7890, 123.456.7890, 1234567890
    const phoneRegex = /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/
    return phoneRegex.test(phone.replace(/\s/g, ''))
  }

  const isCurrentStepValid = () => {
    if (!currentStepData.required) return true
    const value = formData[currentStepData.field as keyof FormData]
    if (!value || value.length === 0) return false
    
    // Additional validation for specific fields
    if (currentStepData.field === 'email') {
      return validateEmail(value)
    }
    if (currentStepData.field === 'phone') {
      return validatePhone(value)
    }
    
    return true
  }

  const progressPercentage = ((currentStep + 1) / activeSteps.length) * 100

  if (isComplete) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#c88546]/10 to-white px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-lg w-full bg-white rounded-2xl shadow-2xl p-8 text-center"
        >
          <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-3xl font-bold mb-4">Thank You!</h2>
          <p className="text-gray-600 mb-6">
            We've received your information and will be in touch within 24 hours.
            Check your email for our free buyer's guide!
          </p>
          <a
            href="tel:9283610298"
            className="flex items-center justify-center gap-2 bg-[#c88546] text-white py-3 px-6 rounded-lg font-semibold text-lg hover:bg-[#b87436] transition-colors mb-8"
          >
            <Phone className="w-5 h-5" />
            Call R.J. Now: (928) 361-0298
          </a>

          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">While you wait, explore:</h3>
            <Button
              onClick={() => window.location.href = '/floor-plans'}
              className="w-full bg-[#c88546] hover:bg-[#b87439] text-white"
            >
              Browse Floor Plans
            </Button>
            <Button
              onClick={() => window.location.href = '/financing'}
              variant="outline"
              className="w-full border-[#c88546] text-[#c88546] hover:bg-[#c88546]/10"
            >
              Financing Options
            </Button>
            <Button
              onClick={() => window.location.href = '/'}
              variant="ghost"
              className="w-full text-gray-600 hover:text-gray-800"
            >
              Back to Home
            </Button>
          </div>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#c88546]/10 to-white px-4 py-12">
      {/* Subtle back link - doesn't distract but available if needed */}
      <Link 
        href="/" 
        className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-700 mb-8 text-sm"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Home
      </Link>
      
      <div className="flex items-center justify-center">
        <div className="max-w-2xl w-full">
          {/* Progress Bar - Hidden for first 2 questions to reduce intimidation */}
        {currentStep >= 2 && (
          <motion.div
            className="mb-8"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-[#c88546]"
                initial={{ width: 0 }}
                animate={{ width: `${progressPercentage}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>
            <p className="text-sm text-gray-600 mt-2">
              Step {currentStep + 1} of {activeSteps.length}
            </p>
          </motion.div>
        )}

        {/* Form Card */}
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          className="bg-white rounded-2xl shadow-2xl p-8 md:p-12"
        >
          {/* Icon */}
          {currentStepData.icon && (
            <div className="w-16 h-16 bg-[#c88546]/10 rounded-xl flex items-center justify-center mb-6">
              <currentStepData.icon className="w-8 h-8 text-[#c88546]" />
            </div>
          )}

          {/* Question */}
          <h2 className="text-2xl md:text-3xl font-bold mb-2">
            {currentStepData.title}
          </h2>
          <p className="text-gray-600 mb-8">
            {currentStepData.subtitle}
          </p>

          {/* Input Field */}
          <AnimatePresence mode="wait">
            {currentStepData.type === 'choice' ? (
              <div className="space-y-3">
                {currentStepData.choices?.map((choice) => (
                  <motion.button
                    key={choice.value}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      handleInputChange(choice.value)
                      setTimeout(handleNext, 200)  // Faster auto-advance
                    }}
                    className={`w-full p-4 rounded-xl border-2 text-left flex items-center gap-4 transition-all ${
                      formData[currentStepData.field as keyof FormData] === choice.value
                        ? 'border-[#c88546] bg-[#c88546]/5'
                        : 'border-gray-200 hover:border-[#c88546]/50'
                    }`}
                  >
                    {choice.icon && <choice.icon className="w-5 h-5 text-[#c88546]" />}
                    <span className="text-lg">{choice.label}</span>
                  </motion.button>
                ))}
              </div>
            ) : currentStepData.type === 'textarea' ? (
              <div>
                <textarea
                  placeholder={currentStepData.placeholder}
                  value={formData[currentStepData.field as keyof FormData] || ''}
                  onChange={(e) => handleInputChange(e.target.value)}
                  rows={4}
                  className="w-full px-6 py-4 text-lg border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#c88546] transition-colors resize-none"
                  autoFocus
                />
                {(currentStepData as any).helperText && (
                  <p className="text-sm text-gray-500 mt-2">
                    {(currentStepData as any).helperText}
                  </p>
                )}
              </div>
            ) : (
              <div>
                <input
                  type={currentStepData.type}
                  placeholder={currentStepData.placeholder}
                  value={formData[currentStepData.field as keyof FormData] || ''}
                  onChange={(e) => handleInputChange(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && isCurrentStepValid()) {
                      handleNext()
                    }
                  }}
                  className="w-full px-6 py-4 text-lg border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#c88546] transition-colors"
                  autoFocus
                />
                {currentStepData.field === 'email' && formData.email && !validateEmail(formData.email) && (
                  <p className="text-sm text-red-500 mt-2">
                    Please enter a valid email address
                  </p>
                )}
                {currentStepData.field === 'phone' && formData.phone && !validatePhone(formData.phone) && (
                  <p className="text-sm text-red-500 mt-2">
                    Please enter a valid phone number (e.g., 928-123-4567)
                  </p>
                )}
                {(currentStepData as any).helperText && (
                  <p className="text-sm text-gray-500 mt-2">
                    {(currentStepData as any).helperText}
                  </p>
                )}
                {(currentStepData as any).privacyMessage && (
                  <p className="text-sm text-green-600 font-medium mt-2">
                    {(currentStepData as any).privacyMessage}
                  </p>
                )}
              </div>
            )}
          </AnimatePresence>

          {/* Navigation Buttons */}
          <div className="flex gap-4 mt-8">
            {currentStep > 0 && (
              <Button
                onClick={handleBack}
                variant="outline"
                className="flex items-center gap-2"
              >
                <ChevronLeft className="w-4 h-4" />
                Back
              </Button>
            )}
            <Button
              onClick={handleNext}
              disabled={currentStepData.required && !isCurrentStepValid()}
              className="flex-1 bg-[#c88546] hover:bg-[#b87439] text-white flex items-center justify-center gap-2"
            >
              {currentStep === activeSteps.length - 1 ? (
                isSubmitting ? 'Submitting...' : 'Send to RJ →'
              ) : currentStep === 0 ? (
                'Submit →'
              ) : currentStepData.id === 'email' ? (
                'Continue →'
              ) : currentStepData.id === 'phone' ? (
                'Continue →'
              ) : currentStepData.id === 'hasLand' ? (
                'Continue →'
              ) : currentStepData.id === 'landAddress' ? (
                'Continue →'
              ) : currentStepData.id === 'interestedArea' ? (
                'Continue →'
              ) : currentStepData.id === 'budget' ? (
                'Continue →'
              ) : (
                <>
                  Continue
                  <ChevronRight className="w-4 h-4" />
                </>
              )}
            </Button>
          </div>
        </motion.div>

        {/* Call R.J. CTA */}
        <div className="mt-8 p-4 bg-[#f5f5f0] rounded-xl border-2 border-[#c88546]/20">
          <p className="text-center text-gray-700 font-medium mb-3">
            Prefer to talk? Get answers faster!
          </p>
          <a
            href="tel:9283610298"
            className="flex items-center justify-center gap-2 bg-[#c88546] text-white py-3 px-6 rounded-lg font-semibold text-lg hover:bg-[#b87436] transition-colors w-full"
          >
            <Phone className="w-5 h-5" />
            Call R.J. Now: (928) 361-0298
          </a>
        </div>
        </div>
      </div>
    </div>
  )
}